<template>
    <city-selector
        :areaData="areaData"
        :isshowHeader="false"
        :selectCompleted.sync="selectCompleted"
        :selectedProvince.sync="selectedProvince"
        :selectedCity.sync="selectedCity"
        :selectedCountry.sync="selectedCountry"
        :selectedAreaType.sync="selectedAreaType"
    />
</template>

<script>
import CitySelector from './index'
import ajax from '@/api/ajax'
import _ from 'lodash'
import { mapMutations, mapGetters } from 'vuex'
import UIP from '@/util/uip.js'
import { LOCATION_CODE_MAP, COUNTRY_CODE } from '@uip-alpha/business-constants'

export default {
    components: {
        'city-selector': CitySelector
    },
    props: {
        updateSelect: {
            type: Function
        },
        updateAreaCode: {
            type: Function
        },
        provinces: {
            type: Array,
            required: true
        },
        updateArea: {
            type: Function,
            default: () => ({})
        },
        area: {
            type: Object,
            default: () => ({})
        },
        page: {
            type: String,
            default: ''
        },
        isDest: {
            type: Boolean,
            default: false
        },
        maxLevel: {
            type: Number,
            default: 3
        }
    },
    data() {
        return {
            selectedProvince: {}, // 选择的省会
            selectedCity: {}, // 选择的城市
            selectedCountry: {}, // 选择的区域
            areaData: {
                provinces: [], // 省会列表
                cities: [], // 城市列表
                countries: [] // 区域列表
            },
            selectCompleted: false,
            selectedAreaType: '' // 当前选择的区域状态
        }
    },
    computed: {
        ...mapGetters('range', {
            getAreaData: 'getAreaData',
            getAreaDataDest: 'getAreaDataDest'
        })
    },
    methods: {
        ...mapMutations('range', {
            setAreaData: 'setAreaData',
            setAreaDataDest: 'setAreaDataDest'
        }),
        initCitySelector({ province, city, country } = {}) {
            // 初始化省会信息
            this.initAreaByNames({ province, city, country })
        },
        async getRegion({ code, level } = {}) {
            const { data } = await ajax.getRegions({
                lang: 'sc',
                regiion: 'cn',
                translate: 'sc',
                params: {
                    level
                },
                data: {
                    code
                }
            })
            const result = this.filterData(data)
            return result
        },
        async initAreaByNames({ province, city, country } = {}) {
            const provinces = this.areaData.provinces
            if (!provinces) {
                return
            }
            if (province) {
                // 初始化城市信息
                this.selectedProvince = this.areaData.provinces.filter(item => item.name === province)[0] || {}
                const cities = await this.getRegion({
                    code: this.selectedProvince.code,
                    level: 2
                })
                if (cities.length <= 0) {
                    return
                }
                this.areaData = { ...this.areaData, cities }
            }
            if (city) {
                // 初始化县区信息
                this.selectedCity = this.areaData.cities.filter(item => item.name === city)[0] || {}
                const countries = await this.getRegion({
                    code: this.selectedCity.code,
                    level: 3
                })
                this.areaData = { ...this.areaData, countries }
            }
            if (country) {
                this.selectedCountry = this.areaData.countries.filter(item => item.name === country)[0] || {}
            }
        },
        filterData(lst) {
            const result = []
            if (Array.isArray(lst)) {
                const ids = new Set()
                _(lst).forEach(item => {
                    if (!ids.has(item.id)) {
                        result.push(item)
                        ids.add(item.id)
                    }
                })
            }
            return result
        }
    },
    watch: {
        selectCompleted(val) {
            this.updateSelect(val)
            this[this.isDest ? 'setAreaDataDest' : 'setAreaData']({
                province: this.selectedProvince,
                city: this.selectedCity,
                country: this.selectedCountry
            })
            this.updateArea({
                province: this.selectedProvince,
                city: this.selectedCity,
                country: this.selectedCountry
            })
        },
        async selectedProvince(newVal, oldVal) {
            // 加载城市信息
            if (Object.keys(newVal || {}).length <= 0) {
                return
            }
            const cities = await this.getRegion({
                code: newVal.code,
                level: 2
            })
            if (cities.length > 0) {
                this.areaData = { ...this.areaData, cities }
                this.selectedAreaType = 'city'
            }
        },
        async selectedCity(newVal, oldVal) {
            // 加载县区信息
            if (Object.keys(newVal || {}).length <= 0) {
                return
            }
            if (UIP.utils.check.AREA.isMO(this.selectedProvince.rateCode)) {
                this.selectCompleted = true
            }
            const countries =
                Number(this.maxLevel) > 2
                    ? await this.getRegion({
                          code: newVal.code,
                          level: 3
                      })
                    : []
            if (countries.length > 0) {
                this.areaData = { ...this.areaData, countries }
                this.selectedAreaType = 'country'
            } else {
                let areaName = [this.selectedProvince.name, this.selectedCity.name, this.selectedCountry.name].join('-')
                if (areaName.endsWith('-')) {
                    areaName = areaName.slice(0, -1)
                }
                const code = this.selectedCity.code
                const rateCode = this.selectedCity.rateCode
                const countryCode = this.selectedCity.countryCode || COUNTRY_CODE.MAINLAND
                this.updateAreaCode({ code, areaName, rateCode, countryCode })
                this.selectCompleted = true
            }
        },
        selectedCountry() {
            let areaName = [this.selectedProvince.name, this.selectedCity.name, this.selectedCountry.name].join('-')
            if (areaName.endsWith('-')) {
                areaName = areaName.slice(0, -1)
            }
            const code = this.selectedCountry.code
            const rateCode = this.selectedCountry.rateCode
            const countryCode = this.selectedCountry.countryCode || COUNTRY_CODE.MAINLAND
            this.updateAreaCode({ code, areaName, rateCode, countryCode })
            this.selectCompleted = true
        }
    },
    created() {
        const provinces = this.provinces.filter(item => [COUNTRY_CODE.HK, COUNTRY_CODE.MAC, COUNTRY_CODE.TW].indexOf(item.code) !== -1)
        if (provinces) {
            this.areaData = { ...this.areaData, provinces }
            let finalData = {}
            const HKMOTWCodeList = [LOCATION_CODE_MAP.HK, LOCATION_CODE_MAP.MAC, LOCATION_CODE_MAP.TW]
            if (this.area.province && ~HKMOTWCodeList.indexOf(this.area.province.rateCode)) {
                finalData = this.isDest ? this.getAreaDataDest : this.getAreaData
            }
            const { province, city, country } = finalData
            if (province && city && country) {
                this.initCitySelector({
                    province: (province || {}).name,
                    city: (city || {}).name,
                    country: (country || {}).name
                })
                this.selectedAreaType = 'country'
            } else if (province && city) {
                this.initCitySelector({
                    province: (province || {}).name,
                    city: (city || {}).name
                })
                this.selectedAreaType = 'city'
            } else {
                this.selectedAreaType = 'province'
            }
        }
    }
}
</script>

<style lang="scss" scoped></style>



// WEBPACK FOOTER //
// src/components/citySelector/area-hmt.vue